import { WatchAccountService } from '../../../../services/watch-account.service';
import { Component, SecurityContext } from '@angular/core';
import { WatchFunnelService } from '../../../../services/watch-funnel.service';
import { FunnelService } from '../../../../services/funnel.service';
import { Subscription, Observable, BehaviorSubject } from 'rxjs';
import { IFunnel } from '../../../../interfaces/graph-types';
import { ChartEvent, ChartType } from '../../../shared/chartist.component';
import * as Chartist from 'chartist';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject } from 'rxjs';

export interface Chart {
  type: string;
  data: Chartist.IChartistData;
  options?: any;
  responsiveOptions?: any;
  events?: ChartEvent;
}

@Component({
  selector: '[account-funnel-dashboard]',
  template: require('./account-funnel-dashboard.component.pug'),
  styles: [require('./account-funnel-dashboard.component.css').toString()],
})
export class AccountFunnelDashboardComponent {
  funnel: IFunnel;
  sub: Subscription;
  public type: ChartType;
  public openEvents: ChartEvent;
  public clickEvents: ChartEvent;
  public unsubEvents: ChartEvent;
  public replyEvents: ChartEvent;
  public bounceEvents: ChartEvent;
  public touchEvents: ChartEvent;
  public applicationEvents: ChartEvent;
  public options: any;
  public optionsBounce: any;

  public optionsStats: any;
  public optionsApplication: any;

  chartistOpenData: any;
  chartistClickData: any;
  chartistReplyData: any;
  chartistUnsubData: any;
  chartistBounceData: any;
  chartistStats: any;
  chartistJouneyProgress: any;
  cacheTotal = null;
  applicationCount = null;
  isLoading = true;

  funnelId: string;
  accountId: string;

  editor = null;
  editorContent = '';
  private bodyHtml: string = null;
  private bodyText: string = null;
  inputHtml: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  resetButton: Subject<boolean> = new Subject<boolean>();

  // -------------------------------------------------------------------
  constructor(
    private watchFunnel: WatchFunnelService,
    private watchAccount: WatchAccountService,
    private funnelService: FunnelService,
    private sanitizer: DomSanitizer,
  ) {}

  // -------------------------------------------------------------------
  ngOnInit(): void {
    this.funnelId = this.watchFunnel.funnelId().getValue();
    this.accountId = this.watchAccount.accountId().getValue();

    this.type = 'Pie';
    this.chartistOpenData = {};
    this.chartistClickData = {};
    this.chartistReplyData = {};
    this.chartistUnsubData = {};
    this.chartistBounceData = {};
    this.chartistStats = {};
    this.chartistJouneyProgress = {};

    this.options = {};
    this.options.donut = true;
    this.options.donutWidth = 30;
    this.options.startAngle = 270;
    this.options.showLabel = false;

    this.optionsBounce = {};
    this.optionsBounce.donut = true;
    this.optionsBounce.donutWidth = 30;
    this.optionsBounce.startAngle = 270;
    this.optionsBounce.showLabel = false;

    this.optionsStats = {};
    this.optionsStats.high = 100;
    this.optionsStats.low = 0;
    this.optionsStats.seriesBarDistance = 10;
    this.optionsStats.height = '250px';
    this.optionsStats.plugins = [Chartist.plugins.tooltip()];
    this.optionsStats.axisY = {
      onlyInteger: true,
      labelInterpolationFnc: function (value) {
        return value + '%';
      },
    };

    this.optionsApplication = {};
    this.optionsApplication.low = 0;
    this.optionsApplication.seriesBarDistance = 10;
    this.optionsApplication.height = '250px';
    this.optionsApplication.plugins = [Chartist.plugins.tooltip()];
    this.optionsApplication.axisY = {
      onlyInteger: true,
    };

    this.openEvents = { draw: this.chartDrawOpenEvent };
    this.clickEvents = { draw: this.chartDrawClickEvent };
    this.replyEvents = { draw: this.chartDrawReplyEvent };
    this.unsubEvents = { draw: this.chartDrawUnsubEvent };
    this.bounceEvents = { draw: this.chartDrawBounceEvent };
    this.touchEvents = { draw: this.chartDrawTouchEvent };
    this.applicationEvents = { draw: this.chartDrawApplicationEvent };

    this.sub = this.funnelService
      .stats(this.funnelId)
      .subscribe(funnel => {
        this.funnel = funnel;

        const safe = this.sanitizer.sanitize(
          SecurityContext.NONE, // TO FIX to prevent script execution on other machines that can see the funnel (Security audit will point that one out). But we don't have text-align and color if we sanitize it :/
          //        SecurityContext.HTML,
          funnel.jobSpec || '<div><br></div>',
        );
        this.editorContent = safe;
        this.bodyHtml = safe;
        this.bodyText = funnel.jobSpec;
        //console.warn('datafeed set inputHtml');
        this.inputHtml.next(safe);  

        this.isLoading = false;
        this.cacheTotal = funnel.cacheTotal;
        this.applicationCount = funnel.applicationCount;
        this.options.total = this.cacheTotal * 2; // * 2 gived us half a donut

        if (this.cacheTotal > 0) {
          this.chartistOpenData.series = [
            funnel.cacheOpen,
            this.cacheTotal - funnel.cacheOpen,
          ];
          this.chartistClickData.series = [
            funnel.cacheClick,
            this.cacheTotal - funnel.cacheClick,
          ];
          this.chartistReplyData.series = [
            funnel.cacheReply,
            this.cacheTotal - funnel.cacheReply,
          ];
          this.chartistUnsubData.series = [
            funnel.cacheUnsub,
            this.cacheTotal - funnel.cacheUnsub,
          ];

          this.optionsBounce.total = (this.cacheTotal + funnel.cacheBounce) * 2; // * 2 gived us half a donut
          this.chartistBounceData.series = [
            funnel.cacheBounce,
            this.cacheTotal,
          ];

          this.chartistStats = funnel.stats;
          this.chartistJouneyProgress = funnel.applicationStats;
        }
      });
  }

  // -------------------------------------------------------------------
  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  // -------------------------------------------------------------------
  onEditorCreated(editor) {
    this.editor = editor;
  }

  // -------------------------------------------------------------------
  onTextChanged({ html, text, firstUpdate }) {
    //console.warn('onTextChanged: ' + text);

    this.bodyHtml = html;
    if (this.bodyHtml) this.bodyHtml.length < 400;
    this.bodyText = text;

    // if (!firstUpdate) this.onFormModified.emit(true);
  }

  // -------------------------------------------------------------------
  save(data: any, onsuccess: onSmartSuccess, onfailure: onSmartFailure) {
    this.funnelService.saveJobSpec(
      this.funnelId,
      this.funnel.jobSpecTitle,
      this.bodyHtml,
      data => {
        onsuccess();
      },
      error => {
        onfailure(error);
      },
    );
  }
  
  // -------------------------------------------------------------------
  chartDrawOpenEvent(context: any): void {
    if (context.index == 0) {
      context.element._node.style.stroke = '#1694ff';
    }
    if (context.index == 1) {
      context.element._node.style.stroke = '#dcdcdc';
    }
  }
  // -------------------------------------------------------------------
  chartDrawClickEvent(context: any): void {
    if (context.index == 0) {
      context.element._node.style.stroke = '#d2b218';
    }
    if (context.index == 1) {
      context.element._node.style.stroke = '#dcdcdc';
    }
  }
  // -------------------------------------------------------------------
  chartDrawReplyEvent(context: any): void {
    if (context.index == 0) {
      context.element._node.style.stroke = '#13d23c';
    }
    if (context.index == 1) {
      context.element._node.style.stroke = '#dcdcdc';
    }
  }
  // -------------------------------------------------------------------
  chartDrawUnsubEvent(context: any): void {
    if (context.index == 0) {
      context.element._node.style.stroke = '#c516ff';
    }
    if (context.index == 1) {
      context.element._node.style.stroke = '#dcdcdc';
    }
  }
  // -------------------------------------------------------------------
  chartDrawBounceEvent(context: any): void {
    if (context.index == 0) {
      context.element._node.style.stroke = '#d70206';
    }
    if (context.index == 1) {
      context.element._node.style.stroke = '#dcdcdc';
    }
  }

  // -------------------------------------------------------------------
  chartDrawTouchEvent(context: any): void {
    if (context.seriesIndex == 0) {
      context.element._node.style.stroke = '#1694ff';
    }
    if (context.seriesIndex == 1) {
      context.element._node.style.stroke = '#d2b218';
    }
    if (context.seriesIndex == 2) {
      context.element._node.style.stroke = '#13d23c';
    }
    if (context.seriesIndex == 3) {
      context.element._node.style.stroke = '#c516ff';
    }
  }

  // -------------------------------------------------------------------
  chartDrawApplicationEvent(context: any): void {
    if (context.seriesIndex == 0) {
      context.element._node.style.stroke = '#ff0000';
    }
  }
}
